import { findByLabel, stripTableQualifier } from './labelMatching'
import type { BuilderFieldOption, DashboardWidget } from './types'

// Client catalog dimension (name/value pair) used to resolve override entries.
export type CatalogDimension = BuilderFieldOption

export type DrilldownPathFilter = {
  field: string
  value: string
}

// A single node in the drilldown chain as returned by
// GET api/agentBuilder/ui/drilldown/dimensions. The backend derives every
// node from the catalog dimension's own DrillLevel / DrillNext metadata; the
// frontend never guesses hierarchy from field names.
export type DrilldownHierarchyNode = {
  field: string
  level: string
  next: string | null
}

// Find the column in `columns` that most closely corresponds to `field`.
// Purely a display-side helper (matches by normalized string) — carries no
// hierarchy assumptions. Prefers an exact normalized match and only falls
// back to a suffix match when no exact match exists anywhere in `columns`,
// so e.g. looking up "SubRegion" against ["Region", "SubRegion"] can't
// collapse into "Region" via a two-way suffix check.
export function findDrilldownColumn(columns: string[], field: string) {
  return findByLabel(columns, field, (column) => [column], 'first-suffix')
}

// Resolve which hierarchy level a field belongs to using the server-provided
// nodes. Returns null when the field isn't part of the platform's hierarchy.
// The backend already supplies exact hierarchy metadata per platform, so this
// only accepts exact normalized matches on the node's field or level — a
// looser suffix match would let e.g. SubRegion collapse into Region.
export function getDrilldownHierarchyLevel(
  hierarchy: DrilldownHierarchyNode[] | null | undefined,
  field: string | null | undefined,
): string | null {
  if (!hierarchy?.length) return null
  const match = findByLabel(hierarchy, field, (node) => [node.field, node.level])
  return match?.level ?? null
}

// ---------------------------------------------------------------------------
// Unified drill order. Every drill question (root, next, membership, mode)
// goes through resolveDrillOrder; the two sources below (override array,
// global server hierarchy) each have exactly one implementation.
// ---------------------------------------------------------------------------

export type DrillMode = 'override' | 'global' | 'disabled'

export interface DrillOrder {
  mode: DrillMode
  /** First drillable field present in `columns`, or null. */
  root(columns: string[]): string | null
  /** Field (as spelled in `columns`) to drill into after `currentField`, or null. */
  next(currentField: string, columns: string[]): string | null
  contains(field: string): boolean
  /** Resolved override order as catalog values; [] for non-override modes. */
  dimensions: string[]
  /** Override entries not in the client's catalog (BA errors); [] otherwise. */
  invalidFields: string[]
}

type Hierarchy = DrilldownHierarchyNode[] | null | undefined

// Global source: walks the server-provided DrillLevel/DrillNext hierarchy.
function createGlobalDrillOrder(hierarchy: Hierarchy): DrillOrder {
  return {
    mode: 'global',
    dimensions: [],
    invalidFields: [],
    contains: (field) => getDrilldownHierarchyLevel(hierarchy, field) !== null,
    // Root = the level no other level points to; falls back to the first
    // hierarchy field in `columns` when the graph is misconfigured.
    root(columns) {
      if (!hierarchy?.length) return null
      const incoming = new Set(hierarchy.map((node) => node.next).filter((next): next is string => Boolean(next)))
      const rootLevel = hierarchy.find((node) => !incoming.has(node.level))?.level
      if (rootLevel) {
        const rootColumn = columns.find((column) => getDrilldownHierarchyLevel(hierarchy, column) === rootLevel)
        if (rootColumn) return rootColumn
      }
      return columns.find((column) => getDrilldownHierarchyLevel(hierarchy, column) !== null) ?? null
    },
    // Level after `currentField`'s level, restricted to columns present.
    next(currentField, columns) {
      const parentLevel = getDrilldownHierarchyLevel(hierarchy, currentField)
      if (!parentLevel || !hierarchy) return null
      const nextLevel = hierarchy.find((node) => node.level === parentLevel)?.next
      if (!nextLevel) return null
      return columns.find((column) => getDrilldownHierarchyLevel(hierarchy, column) === nextLevel) ?? null
    },
  }
}

function findCatalogDimension(catalog: CatalogDimension[], entry: string) {
  // Catalog is authoritative: match by value, then by name.
  return findByLabel(catalog, entry, (dimension) => [dimension.value])
    ?? findByLabel(catalog, entry, (dimension) => [dimension.name])
}

function findEntryColumn(columns: string[], dimension: CatalogDimension) {
  return findByLabel(columns, dimension.value, (column) => [column])
    ?? findByLabel(columns, dimension.name, (column) => [column])
    ?? findByLabel(columns, stripTableQualifier(dimension.value), (column) => [column])
    ?? findDrilldownColumn(columns, dimension.value)
}

// Override source: index i drills into index i + 1 of the resolved array.
function createOverrideDrillOrder(resolved: CatalogDimension[]): DrillOrder {
  const indexOf = (field: string) => {
    const match = findByLabel(resolved, field, (dimension) => [dimension.value])
      ?? findByLabel(resolved, field, (dimension) => [dimension.name])
    return match ? resolved.indexOf(match) : -1
  }
  return {
    mode: 'override',
    dimensions: resolved.map((dimension) => dimension.value),
    invalidFields: [],
    contains: (field) => indexOf(field) >= 0,
    root(columns) {
      for (const dimension of resolved) {
        const column = findEntryColumn(columns, dimension)
        if (column) return column
      }
      return null
    },
    next(currentField, columns) {
      const index = indexOf(currentField)
      if (index < 0) return null
      for (const dimension of resolved.slice(index + 1)) {
        const column = findEntryColumn(columns, dimension)
        if (column) return column
      }
      return null
    },
  }
}

export function createDisabledDrillOrder(invalidFields: string[]): DrillOrder {
  return {
    mode: 'disabled',
    dimensions: [],
    invalidFields,
    contains: () => false,
    root: () => null,
    next: () => null,
  }
}

// Single source-selection point. Precedence: override -> global. A widget that
// declares an override never degrades to global: it drills its own path or
// offers no drill (disabled). Legacy `drillDimensions` is deliberately not a
// source (owned by the separate runtime.ts).
export function resolveDrillOrder(
  widget: DashboardWidget,
  hierarchy: Hierarchy,
  clientCatalog: CatalogDimension[],
): DrillOrder {
  const entries = (widget.drillDownDimensions ?? [])
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter(Boolean)
  if (!entries.length) return createGlobalDrillOrder(hierarchy)

  const resolved: CatalogDimension[] = []
  const invalid: string[] = []
  for (const entry of entries) {
    const dimension = findCatalogDimension(clientCatalog ?? [], entry)
    if (!dimension) invalid.push(entry)
    else if (!resolved.includes(dimension)) resolved.push(dimension)
  }
  if (invalid.length || resolved.length < 2) return createDisabledDrillOrder(invalid)
  return createOverrideDrillOrder(resolved)
}

// Dimensions a table-like widget offers in its field selector. They come from
// the widget's own dimensions[]; the global hierarchy never narrows them. Only
// a widget that declares its own drillDownDimensions (override) limits the
// selector to that list, so a selected field can always drill.
export function getFieldSelectionDimensions<T extends { value?: string; name: string }>(
  dimensions: T[],
  drillOrder: Pick<DrillOrder, 'mode' | 'contains'>,
  isTableLike: boolean,
): T[] {
  if (!isTableLike || drillOrder.mode !== 'override') return dimensions
  return dimensions.filter((dimension) => drillOrder.contains(dimension.value || dimension.name))
}

// Thin wrappers over the global source, kept only so existing callers
// (AgentBuilderWidgetPanel.tsx) compile while they migrate to
// resolveDrillOrder. Do not add new callers.
export function getNextDrilldownField(hierarchy: Hierarchy, columns: string[], parentField: string) {
  return createGlobalDrillOrder(hierarchy).next(parentField, columns)
}

export function isDrilldownFieldAllowed(hierarchy: Hierarchy, field: string) {
  return createGlobalDrillOrder(hierarchy).contains(field)
}

export function getInitialDrilldownField(hierarchy: Hierarchy, columns: string[]) {
  return createGlobalDrillOrder(hierarchy).root(columns)
}

export function getRowDrilldownPath(
  hierarchy: DrilldownHierarchyNode[] | null | undefined,
  row: Record<string, unknown>,
  columns: string[],
): DrilldownPathFilter[] {
  const path: DrilldownPathFilter[] = []
  const field = getInitialDrilldownField(hierarchy, columns)
  if (!field) return path
  const value = row[field]
  if (value !== null && value !== undefined && String(value).trim()) {
    path.push({ field, value: String(value) })
  }
  return path
}
